import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { sensorElementConfigs } from "../../../Configurations/SensorConfigurations/sensorElements/sensorElementConfigs.js";
import { temperatureElementConfig } from "../../../Configurations/SensorConfigurations/sensorElements/temperatureSensorElement.js";
import { UnitCategoryConfigurations } from "../../../Configurations/Units/UnitCategory/UnitCategoryConfigurations.js";
import { UnitConfigurations } from "../../../Configurations/Units/UnitConfigurations.js";
import { canConvertUnits } from "../../../Configurations/Units/UnitConversion.js";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { SensorElementAddress } from "../../commonSubSchemas/sensorElementAddress.js";
import type { SensorElementConfigKey } from "../../sensorElement/sensorElement.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    HeatAndFrostProtectionFieldsWithDefaults,
    HeatAndFrostProtectionSchemaTypes,
} from "../../types/heatAndFrostProtection.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "HeatAndFrostProtection" as const;

const [LIMIT_MIN_CELSIUS, LIMIT_MAX_CELSIUS] = temperatureElementConfig.legalConvertedRange as [number, number];

const limitCelsius = z.number().min(LIMIT_MIN_CELSIUS).max(LIMIT_MAX_CELSIUS);

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<HeatAndFrostProtection>>(...addSchemaKeyHook(SCHEMA_KEY))
export class HeatAndFrostProtection extends SchemaBase<
    HeatAndFrostProtection,
    HeatAndFrostProtectionFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.boolean() })
    @prop({ type: () => Boolean, required: true })
    public enabled!: boolean;

    /** The station's temperature element that is watched, as the station's own sensor DTO gives it. */
    @validate({ zodValidator: SensorElementAddress.dtoValidators.base.optional() })
    @prop({ type: () => SensorElementAddress, required: false, _id: false })
    public sensorElementAddress?: SensorElementAddress;

    @validate({ zodValidator: limitCelsius.nullable().optional() })
    @prop({ type: () => Number, required: false, min: LIMIT_MIN_CELSIUS, max: LIMIT_MAX_CELSIUS })
    public frostLimitCelsius?: number | null;

    @validate({ zodValidator: limitCelsius.nullable().optional() })
    @prop({ type: () => Number, required: false, min: LIMIT_MIN_CELSIUS, max: LIMIT_MAX_CELSIUS })
    public heatLimitCelsius?: number | null;

    static getters = {} as const satisfies Record<string, (self: HeatAndFrostProtection) => any>;

    /** The bounds on a limit, in Celsius. */
    static readonly limitRangeCelsius = [LIMIT_MIN_CELSIUS, LIMIT_MAX_CELSIUS] as const;

    static utilities = {
        /** Whether an element can be watched: its unit category's stored unit converts to Celsius. */
        isWatchable: (sensorElementConfigKey: SensorElementConfigKey): boolean => {
            const elementConfig = sensorElementConfigs[sensorElementConfigKey];
            if (!elementConfig) return false;

            const { databaseUnit } = UnitCategoryConfigurations[elementConfig.unitCategory];
            return canConvertUnits(UnitConfigurations[databaseUnit].mathjs, UnitConfigurations.celsius.mathjs);
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<HeatAndFrostProtectionSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<HeatAndFrostProtection, HeatAndFrostProtectionFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

HeatAndFrostProtection.dtoValidators = getZodSchemaFromClass(
    HeatAndFrostProtection,
) as unknown as DtoValidatorTypes<HeatAndFrostProtectionSchemaTypes>;
